import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import SlotPicker from "../components/SlotPicker";
import SpiritualDisclaimer from "../components/SpiritualDisclaimer";

export default function HouseCalls() {
  const [fees, setFees] = useState({});
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", parish: "",
    service_requested: "house-cleansing",
    travel_tier: "local",
    travel_acknowledged: false,
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get("/travel-fees").then((r) => setFees(r.data));
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.category === "House Calls")));
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.preferred_date || !form.preferred_time) return toast.error("Please pick a date and time slot");
    if (!agreed) return toast.error("Please agree to the Terms & Disclaimer to continue");
    setLoading(true);
    try {
      const { data: hc } = await api.post("/house-calls", form);
      // If travel tier is custom (free quote), skip payment
      if (form.travel_tier === "custom") {
        toast.success("House call request received. The Lion Chief will contact you with a custom quote.");
        setForm({ ...form, name: "", phone: "", email: "", address: "", preferred_date: "", notes: "" });
        setLoading(false);
        return;
      }
      const { data: checkout } = await api.post("/checkout/session", {
        item_type: "housecall",
        item_id: hc.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const svc = services.find((s) => s.id === form.service_requested);
  const fee = fees[form.travel_tier];
  const total = (svc?.price || 0) + (fee?.fee || 0);

  return (
    <div className="py-20 md:py-28 px-6 md:px-10 max-w-5xl mx-auto" data-testid="house-calls-page">
      <div className="overline mb-4">In-Person Services</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Request a House Call</h1>
      <p className="mt-6 text-white/70 max-w-2xl text-lg">
        The Lion Chief travels to your home for cleansings, protection work, and uncrossing rituals. Local, regional, and custom travel options available.
      </p>

      <SpiritualDisclaimer />

      <form onSubmit={submit} className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="house-call-form">
        <div className="md:col-span-2">
          <label className="overline block mb-3">Service Requested</label>
          <select name="service_requested" value={form.service_requested} onChange={onChange} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none" data-testid="hc-service-select">
            {services.map((s) => (
              <option key={s.id} value={s.id} className="bg-black">{s.name} — ${s.price.toFixed(2)}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-3">Travel Tier</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="hc-travel-tier">
            {Object.entries(fees).map(([key, val]) => (
              <label key={key} className={`border p-4 cursor-pointer transition-colors ${form.travel_tier === key ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/15"}`} data-testid={`hc-travel-${key}`}>
                <input type="radio" name="travel_tier" value={key} checked={form.travel_tier === key} onChange={onChange} className="sr-only" />
                <div className="font-serif text-lg">{val.label}</div>
                <div className="text-xs text-white/60 mt-1">{val.fee > 0 ? `+ $${val.fee.toFixed(2)}` : "Custom Quote"}</div>
              </label>
            ))}
          </div>
        </div>

        <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testid="hc-name" />
        <Field label="Phone" name="phone" value={form.phone} onChange={onChange} required testid="hc-phone" />
        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="hc-email" />
        <div /> {/* spacer */}

        <div className="md:col-span-2">
          <Field label="Physical Address" name="address" value={form.address} onChange={onChange} required testid="hc-address" />
        </div>
        <Field label="Parish / City" name="parish" value={form.parish} onChange={onChange} required testid="hc-parish" />
        <div /> {/* spacer */}

        <div className="md:col-span-2">
          <label className={`flex items-start gap-3 cursor-pointer text-sm ${form.travel_tier === "custom" ? "opacity-50" : ""}`} data-testid="hc-travel-ack">
            <input type="checkbox" checked={form.travel_acknowledged} onChange={(e) => setForm({ ...form, travel_acknowledged: e.target.checked })} disabled={form.travel_tier === "custom"} className="mt-1" data-testid="hc-travel-ack-checkbox" />
            <span className="text-white/70">
              {form.travel_tier === "custom"
                ? "Outside NOLA/Lafayette — The Lion Chief will contact you with a custom travel quote before booking is confirmed."
                : `I acknowledge the ${fees[form.travel_tier]?.label} travel fee of $${fees[form.travel_tier]?.fee?.toFixed(2) || "—"} will be added to the service price.`}
            </span>
          </label>
        </div>

        <div className="md:col-span-2">
          <SlotPicker
            serviceId={form.service_requested}
            value={{ date: form.preferred_date, time: form.preferred_time }}
            onChange={(v) => setForm({ ...form, preferred_date: v.date, preferred_time: v.time })}
          />
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-3">Additional Notes</label>
          <textarea name="notes" value={form.notes} onChange={onChange} rows={4} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none" data-testid="hc-notes" />
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-white/10">
          <label className="flex items-start gap-3 text-sm text-white/70 cursor-pointer" data-testid="hc-agree">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="hc-agree-checkbox" />
            <span>I agree to the <a href="/terms" target="_blank" className="text-[#D4AF37] underline">Terms</a>, <a href="/cancellation-policy" target="_blank" className="text-[#D4AF37] underline">Cancellation</a>, and <a href="/disclaimer" target="_blank" className="text-[#D4AF37] underline">Disclaimer</a>.</span>
          </label>
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-white/10">
          <div>
            <div className="overline text-[#D4AF37]">Estimated Total</div>
            <div className="font-serif text-3xl">{form.travel_tier === "custom" ? "Custom Quote" : `$${total.toFixed(2)}`}</div>
          </div>
          <button type="submit" disabled={loading} className="bg-[#8B0000] hover:bg-[#A50000] text-white px-10 py-4 text-xs uppercase tracking-[0.2em]" data-testid="hc-submit-btn">
            {loading ? "Submitting…" : form.travel_tier === "custom" ? "Submit Request" : "Continue to Payment"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline block mb-3">{label}{required && " *"}</label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none" data-testid={testid} />
    </div>
  );
}
